import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getPublicApiOrigin } from '../config/env';

const rawBase = getPublicApiOrigin();
const BASE = rawBase === '/api' || rawBase === '/api/' ? '' : rawBase.replace(/\/$/, '');

const api = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function Badge({ children, color = '#6b7280', bg = '#f3f4f6' }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: bg, color, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function CandidateRow({ c }) {
  return (
    <tr style={{ background: c.is_shortlisted ? '#fffbeb' : '#fff', borderBottom: '1px solid #f0f0f0' }}>
      <td style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
            {(c.name || '?').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{c.name || '—'}</div>
            {c.is_shortlisted && <div style={{ fontSize: 11, color: '#b45309', fontWeight: 700 }}>★ Shortlisted</div>}
          </div>
        </div>
      </td>
      <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>{c.phone || '—'}</td>
      <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>{c.email || '—'}</td>
      <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>{c.experience_years || '—'}</td>
      <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>{c.current_ctc || '—'}</td>
      <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>{c.notice_period || '—'}</td>
      <td style={{ padding: '10px 12px' }}>
        {c.is_shortlisted
          ? <Badge color="#b45309" bg="#fef3c7">★ Shortlisted</Badge>
          : <Badge color="#6b7280" bg="#f3f4f6">Pending</Badge>}
      </td>
    </tr>
  );
}

function PositionCard({ pos, isOpen, onToggle }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    api.get(`/api/integration/positions/${pos.id}/candidates`)
      .then((res) => setDetail(res.data))
      .catch((e) => console.error('Failed to load candidates', e))
      .finally(() => setLoading(false));
  }, [isOpen, pos.id]);

  const open = () => onToggle();

  const shortlisted = detail?.candidates?.filter((c) => c.is_shortlisted) || [];
  const others = detail?.candidates?.filter((c) => !c.is_shortlisted) || [];

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      {/* Header row */}
      <button
        type="button"
        onClick={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontSize: 18, color: '#6b7280', minWidth: 18 }}>{isOpen ? '▾' : '▸'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{pos.title}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>ReachOut Position #{pos.reachout_position_id}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Badge color="#374151" bg="#f3f4f6">{pos.total_candidates} candidates</Badge>
          {pos.shortlisted_candidates > 0 && (
            <Badge color="#b45309" bg="#fef3c7">★ {pos.shortlisted_candidates} shortlisted</Badge>
          )}
          <Badge color={pos.status === 'pending_video' ? '#0369a1' : '#059669'} bg={pos.status === 'pending_video' ? '#e0f2fe' : '#ecfdf5'}>
            {pos.status === 'pending_video' ? 'Pending Interview' : pos.status}
          </Badge>
        </div>
      </button>

      {/* Expanded candidate list */}
      {isOpen && (
        <div style={{ borderTop: '1px solid #f0f0f0' }}>
          {loading && <div style={{ padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading candidates…</div>}
          {detail && (
            <>
              {/* Shortlisted */}
              <div style={{ padding: '10px 18px 4px', display: 'flex', alignItems: 'center', gap: 8, background: '#fef9eb' }}>
                <span style={{ fontWeight: 700, fontSize: 12, color: '#b45309' }}>★ Shortlisted</span>
                <Badge color="#92400e" bg="#fde68a">{shortlisted.length}</Badge>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    {['Candidate', 'Phone', 'Email', 'Experience', 'CTC', 'Notice', 'Status'].map((h) => (
                      <th key={h} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shortlisted.length === 0
                    ? <tr><td colSpan={7} style={{ padding: '12px 18px', color: '#9ca3af', fontSize: 13 }}>No shortlisted candidates yet</td></tr>
                    : shortlisted.map((c) => <CandidateRow key={c.id} c={c} />)}
                </tbody>
              </table>

              {/* All candidates toggle */}
              {others.length > 0 && (
                <>
                  <div style={{ padding: '10px 18px 4px', display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', borderTop: '1px solid #f0f0f0' }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: '#374151' }}>All Candidates</span>
                    <Badge color="#334155" bg="#e2e8f0">{detail.candidates.length}</Badge>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#fafafa' }}>
                        {['Candidate', 'Phone', 'Email', 'Experience', 'CTC', 'Notice', 'Status'].map((h) => (
                          <th key={h} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.candidates.map((c) => <CandidateRow key={c.id} c={c} />)}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReachOutPositions() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(null);

  const loadPositions = () => {
    setLoading(true);
    setError(null);
    api.get('/api/integration/positions')
      .then((r) => setPositions(r.data))
      .catch(() => setError('Failed to load positions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPositions(); }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>ReachOut Positions</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>Positions and candidates synced from ReachOut</p>
        </div>
        <button
          onClick={loadPositions}
          disabled={loading}
          style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
        >
          ↻ Refresh
        </button>
      </div>

      {loading && <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>}
      {error && <div style={{ color: '#dc2626', fontSize: 14 }}>{error}</div>}

      {!loading && positions.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af', fontSize: 14 }}>No positions synced from ReachOut yet.</div>
      )}

      {positions.map((pos) => (
        <PositionCard
          key={pos.id}
          pos={pos}
          isOpen={openId === pos.id}
          onToggle={() => setOpenId((prev) => (prev === pos.id ? null : pos.id))}
        />
      ))}
    </div>
  );
}
